import type { CustomerQuote, Quote } from '@talos/kyoko';
import type { ModifyFixingState } from './modifyFixingSlice';

export interface EditableFixingDetail {
  Index?: string;
  Fixing?: string;
  Price?: string;
  Amount?: string;
}

export type EditableFixingDetails =
  | { MultilegFixingDetails: Record<string, EditableFixingDetail> } // multi-token
  | { Index: string; Fixing: string }; // non-compound (eg spot)

export interface UpdateFixingPriceBody {
  Price?: string;
  Amount?: string;
  FixingDetails: EditableFixingDetails;
}

export function buildUpdateFixingPriceBody({
  quote,
  state,
}: {
  quote: Quote | CustomerQuote;
  state: ModifyFixingState;
}): UpdateFixingPriceBody {
  if (!quote.FixingDetails) {
    throw new Error('Failed to find FixingDetails on entity');
  }

  if ('MultilegFixingDetails' in quote.FixingDetails) {
    const multilegFixingDetails = state.entries.reduce<Record<string, EditableFixingDetail>>((acc, entry) => {
      if (
        entry.symbol == null ||
        entry.index == null ||
        entry.fixingField.value == null ||
        entry.priceField.value == null ||
        entry.amountField.value == null
      ) {
        throw new Error('Missing properties on fixing legs');
      }

      acc[entry.symbol] = {
        Index: entry.index,
        Fixing: entry.fixingField.value,
        Price: entry.priceField.value,
        Amount: entry.amountField.value,
      };
      return acc;
    }, {});

    const body: UpdateFixingPriceBody = {
      FixingDetails: {
        MultilegFixingDetails: multilegFixingDetails,
      },
    };

    const canModifyTopLevel = canModifyTopLevelFixingEconomics(quote);
    if (canModifyTopLevel) {
      body.Price = state.topLevelPriceField.value;
      body.Amount = state.topLevelAmountField.value;
    }

    return body;
  }

  // Else, for entries without MultilegFixingDetails (eg spot)
  const entry = state.entries.at(0);
  if (
    entry?.index == null ||
    entry?.priceField.value == null ||
    entry?.amountField.value == null ||
    entry?.fixingField.value == null
  ) {
    throw new Error('Missing properties on fixing');
  }

  return {
    Price: entry.priceField.value,
    Amount: entry.amountField.value,
    FixingDetails: {
      Index: entry.index,
      Fixing: entry.fixingField.value,
    },
  };
}

/** Given a Quote/CustomerQuote, determines if the user should be able to modify the top-level fixing economics */
export function canModifyTopLevelFixingEconomics(quote: Quote | CustomerQuote): boolean {
  return !!quote.FixingDetails?.MultilegFixingDetails;
}
